import { ChannelChip } from "../ui/channel.tsx";
import { ListBulletsIcon, ChartBarHorizontalIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { localDate } from "@zooi/domain";
import { Fragment, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { q } from "../api.ts";
import { CampaignTable } from "../campaign-table.tsx";
import { CAMPAIGN_VIEW_KEY, campaignRows, groupCampaignActions, phaseStatus, playbooksOf, rangeOfRows, readCampaignView, resultStatus, sortCampaigns, tabOf, windowProgress, type CampaignView } from "../campaign-items.ts";
import { dateTime } from "../format.ts";
import { AREA_LABELS as A, CAMPAIGN_LABELS as B, INBOX_LABELS as I, KIT_LABELS, STATUS_LABELS } from "../labels.ts";
import { MetricSummary } from "../metrics.tsx";
import { GoalBar, Goals } from "../goals.tsx";
import { readPref, writePref } from "../prefs.ts";
import { Callout, ErrorText, Kbd, Page, Quiet, Section, TabPanel, Tabs, ViewSwitch } from "../ui.tsx";
import { GroupHeader, ListRow } from "../ui/list.tsx";
import { MetricChip, WindowTimeline } from "../ui/measure.tsx";
import { PropertyList, PropertyRow } from "../ui/props.tsx";
import { AgentText } from "../ui/review.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { ResultBadge, StatusChip } from "../ui/status.tsx";

// A product's campaigns (docs/design-system.md §0.4a, plan M7 step 7i), after
// Linear's projects: active, closed and goals, with the goals they answer to
// on top of the active ones; a list or a timeline of their observation
// windows; and each campaign as a full page with its properties
// beside it. A campaign's reading is confirmed on its own review page; A opens
// it when one is due.

const VIEW_KEY = CAMPAIGN_VIEW_KEY;
const narrow = () => window.matchMedia("(max-width: 860px)").matches;

/** `/p/:product/campaigns?view=`: the closed campaigns or the goals instead of the active campaigns. */
export const campaignsSearch = (search: Record<string, unknown>): { view?: "closed" | "goals" } => (search.view === "closed" || search.view === "goals" ? { view: search.view } : {});

/** `/p/:product/campaigns/:id?view=table` opens the campaign's table (plan M8 step 8d). */
export const campaignSearch = (search: Record<string, unknown>): { view?: "table" } => (search.view === "table" ? { view: "table" } : {});

/** When a campaign's window matures, how long it is, or when it closed; the phase itself is said beside it. */
export function windowMeta(campaign: C.CampaignOverview, zone: string) {
  if (campaign.closed_at) return B.closedOn(dateTime(campaign.closed_at, zone));
  if (campaign.phase === "measuring" && campaign.matures_at) return B.matures(dateTime(campaign.matures_at, zone));
  return I.windowDays(campaign.window_days);
}

export function CampaignsPage() {
  const navigate = useNavigate();
  const { productId } = useParams({ strict: false }) as { productId: string };
  const search = useSearch({ strict: false }) as { view?: "closed" | "goals" };
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const board = useQuery(q.campaignBoard(productId));
  const tab = search.view ?? "active";
  const all = board.data?.campaigns ?? [];
  const campaigns = sortCampaigns(all.filter((campaign) => tab !== "goals" && tabOf(campaign) === tab));
  const counts = { active: all.filter((campaign) => tabOf(campaign) === "active").length, closed: all.filter((campaign) => tabOf(campaign) === "closed").length };
  const [chosen, setChosen] = useState<CampaignView>(() => readCampaignView(readPref(VIEW_KEY)));
  const [wide, setWide] = useState(() => !narrow());
  useEffect(() => {
    const query = window.matchMedia("(max-width: 860px)");
    const update = () => setWide(!query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  // Narrow screens have no timeline, only the list.
  const view: CampaignView = wide ? chosen : "list";
  const [cursor, setCursor] = useState<string | null>(null);
  const rows = useRef(new Map<string, HTMLLIElement>());
  const current = campaigns.find((campaign) => campaign.id === cursor) ?? campaigns[0];
  const zone = product?.settings.timezone ?? "UTC";
  const today = localDate(Date.now(), zone);

  const setTab = (next: "active" | "closed" | "goals") => { setCursor(null); void navigate({ to: "/p/$productId/campaigns", params: { productId }, search: next === "active" ? {} : { view: next }, replace: true }); };
  const setView = (next: CampaignView) => { setChosen(next); writePref(VIEW_KEY, next); };
  const open = (campaign: C.CampaignOverview) => { void navigate({ to: "/p/$productId/campaigns/$campaignId", params: { productId, campaignId: campaign.id } }); };
  const read = (campaign: C.CampaignOverview) => { void navigate({ to: "/p/$productId/campaigns/$campaignId/reading", params: { productId, campaignId: campaign.id }, search: { intent: "approve" } }); };
  const move = (delta: number) => {
    if (!campaigns.length) return;
    const index = Math.max(0, campaigns.findIndex((campaign) => campaign.id === current?.id));
    const next = campaigns[Math.min(campaigns.length - 1, Math.max(0, index + delta))];
    setCursor(next.id);
    rows.current.get(next.id)?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (key === "j" || key === "ArrowDown") move(1);
    else if (key === "k" || key === "ArrowUp") move(-1);
    else if (key === "Home") move(-campaigns.length);
    else if (key === "End") move(campaigns.length);
    else if (key === "Enter" && current) open(current);
    else if (key === "a" && current?.phase === "reading_due") read(current);
    else return;
    event.preventDefault();
  };

  const crumbs = [{ label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } }, { label: A.campaigns }];
  if (board.isLoading) return <Page wide crumbs={crumbs}><Skeleton /></Page>;
  const empty = tab === "active"
    ? <Quiet title={B.emptyOpenTitle}><p>{B.emptyOpenNote}</p></Quiet>
    : <Quiet title={B.emptyClosedTitle}><p>{B.emptyClosedNote}</p></Quiet>;
  const list = <ul className="z-rows z-boxed" role="listbox" aria-label={A.campaigns} onKeyDown={onKeyDown}>
    {campaigns.map((campaign) => <ListRow key={campaign.id} id={`campaign-${campaign.id}`} selected={campaign.id === current?.id}
      status={phaseStatus(campaign.phase)} progress={campaign.phase === "measuring" ? windowProgress(campaign, today, zone) : undefined}
      title={campaign.title} sub={B.phase[campaign.phase].label}
      chips={<MetricChip>{B.metrics(campaign.metrics[0]?.name ?? "–", Math.max(0, campaign.metrics.length - 1))}</MetricChip>}
      meta={windowMeta(campaign, zone)}
      onSelect={() => setCursor(campaign.id)} onOpen={() => open(campaign)}
      rowRef={(node) => { if (node) rows.current.set(campaign.id, node); else rows.current.delete(campaign.id); }} />)}
  </ul>;
  const timelineRows = campaignRows(campaigns, zone, B.phase.not_started.label);
  const body = campaigns.length === 0 ? empty
    : view === "timeline" ? <WindowTimeline range={rangeOfRows(timelineRows, today)} today={today} rows={timelineRows} />
    : list;
  return <Page wide crumbs={crumbs} title={A.campaigns} actions={tab !== "goals" && campaigns.length > 0 && view === "list" && <span className="z-inbox-keys">{B.keys}</span>}>
    <ErrorText error={board.error} />
    <Tabs value={tab} onValueChange={setTab}
      tabs={[{ value: "active", label: B.open, count: counts.active }, { value: "closed", label: B.closed, count: counts.closed }, { value: "goals", label: B.goals }]}
      tools={tab !== "goals" && wide && <ViewSwitch label={B.view} value={chosen} onValueChange={setView}
        options={[{ value: "list", label: B.list, icon: <ListBulletsIcon size={14} /> }, { value: "timeline", label: B.timeline, icon: <ChartBarHorizontalIcon size={14} /> }]} />}>
      <TabPanel value="active">{tab === "active" && <div className="z-flow">{product && <GoalBar productId={product.id} timeZone={zone} campaigns={all} />}<div>{body}</div></div>}</TabPanel>
      <TabPanel value="closed">{tab === "closed" && body}</TabPanel>
      <TabPanel value="goals">{tab === "goals" && product && <Goals productId={product.id} timeZone={zone} businessModel={product.business_model} />}</TabPanel>
    </Tabs>
  </Page>;
}

/**
 * One campaign as a page: the reading when it is due, the hypothesis, the
 * latest reading, its metrics over the window, its actions and earlier
 * readings, with its properties beside it. The table tab shows the units its
 * playbooks act on, candidates and actions together (plan M8 step 8d).
 */
export function CampaignPage() {
  const navigate = useNavigate();
  const { productId, campaignId } = useParams({ strict: false }) as { productId: string; campaignId: string };
  const search = useSearch({ strict: false }) as { view?: "table" };
  const view = search.view === "table" ? "table" : "overview";
  const setView = (next: "overview" | "table") => { void navigate({ to: "/p/$productId/campaigns/$campaignId", params: { productId, campaignId }, search: next === "table" ? { view: next } : {}, replace: true }); };
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const detail = useQuery(q.campaign(campaignId));
  const zone = product?.settings.timezone ?? "UTC";
  const data = detail.data;
  const due = data?.campaign.phase === "reading_due";
  // A opens the reading's review when it is due; Esc goes back to the list. Never while typing; the table has its own keys.
  useEffect(() => {
    if (view === "table") return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable], [role=dialog]")) return;
      if (event.key.toLowerCase() === "a" && due) { event.preventDefault(); void navigate({ to: "/p/$productId/campaigns/$campaignId/reading", params: { productId, campaignId }, search: { intent: "approve" } }); }
      else if (event.key === "Escape") { event.preventDefault(); void navigate({ to: "/p/$productId/campaigns", params: { productId } }); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [due, navigate, productId, campaignId, view]);
  const crumbs = [
    { label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } },
    { label: A.campaigns, to: "/p/$productId/campaigns", params: { productId } },
    { label: data?.campaign.title ?? B.detail },
  ];
  if (detail.isLoading) return <Page wide crumbs={crumbs}><Skeleton shape="object" /></Page>;
  if (!data) return <Page crumbs={crumbs}><ErrorText error={detail.error} /></Page>;
  const campaign = data.campaign;
  const latest = data.readings[0] ?? null;
  const earlier = data.readings.slice(1);
  const playbooks = playbooksOf(data.actions);
  const groups = groupCampaignActions(data.actions);
  return <Page wide crumbs={crumbs} title={campaign.title}
    meta={<><StatusChip kind={phaseStatus(campaign.phase)}>{B.phase[campaign.phase].label}</StatusChip><span>{windowMeta(campaign, zone)}</span></>}>
    <Tabs value={view} onValueChange={setView} tabs={[{ value: "overview", label: B.overview }, { value: "table", label: B.table }]}>
      <TabPanel value="overview">{view === "overview" && <div className="z-layout-main">
        <div className="z-flow">
          {due && <Callout>
            <div className="z-flow">
              <p>{B.readingDueNote}</p>
              <p><Link to="/p/$productId/campaigns/$campaignId/reading" params={{ productId, campaignId }} search={{ intent: "approve" }} className="z-btn primary">{B.confirmReading} <Kbd>A</Kbd></Link></p>
            </div>
          </Callout>}
          <Section title={I.hypothesis}><AgentText label={B.hypothesisBy} lines={4}>{data.hypothesis}</AgentText></Section>
          <Section title={B.latestReading}>
            {data.draft
              ? <p className="z-inline"><StatusChip kind="due">{STATUS_LABELS.due}</StatusChip><span>{B.draftWaitingShort(I.nextSteps[data.draft.next_step])}</span><Link className="z-link" to="/p/$productId/campaigns/$campaignId/reading" params={{ productId, campaignId }}>{B.goConfirm}</Link></p>
              : latest ? <ReadingLine reading={latest} zone={zone} />
              : <p className="z-empty">{B.noReadings}</p>}
          </Section>
          <Section title={I.metrics}>
            {data.measurement ? <MetricSummary result={data.measurement} /> : <p className="z-small z-muted">{B.unmeasurable}</p>}
          </Section>
          <Section title={A.actions} count={data.actions.length || undefined}>
            {data.actions.length ? <div className="z-boxed">{groups.map(([kind, members]) => <Fragment key={kind}>
              <GroupHeader label={STATUS_LABELS[kind]} count={members.length} status={kind} />
              {members.map((action) => <ListRow key={action.id} to={`/p/${productId}/actions/${action.id}`} status={kind} title={action.title}
                sub={I.actionKind[action.kind] ?? action.kind} chips={<ChannelChip channel={action.channel} />} />)}
            </Fragment>)}</div> : <p className="z-empty">{B.noActions}</p>}
          </Section>
          {earlier.length > 0 && <Section title={B.earlierReadings} count={earlier.length}>
            <ul className="z-flow">{earlier.map((reading) => <li key={reading.id}><ReadingLine reading={reading} zone={zone} /></li>)}</ul>
          </Section>}
        </div>
        <aside className="z-detail-summary" aria-label={KIT_LABELS.properties}>
          <h2>{KIT_LABELS.properties}</h2>
          <PropertyList>
            <PropertyRow label={B.phaseLabel}><StatusChip kind={phaseStatus(campaign.phase)}>{B.phase[campaign.phase].label}</StatusChip></PropertyRow>
            <PropertyRow label={I.goal}>{data.goal && <span>{data.goal.statement}{data.goal.deadline && <span className="z-muted"> · {B.deadline(data.goal.deadline, zone)}</span>}</span>}</PropertyRow>
            <PropertyRow label={I.metrics}>{campaign.metrics.length > 0 && <span className="z-inline">{campaign.metrics.map((metric) => <MetricChip key={metric.id}>{metric.name}</MetricChip>)}</span>}</PropertyRow>
            <PropertyRow label={B.windowStart}>{campaign.window_start && dateTime(campaign.window_start, zone)}</PropertyRow>
            <PropertyRow label={B.windowMatures}>{campaign.matures_at && dateTime(campaign.matures_at, zone)}</PropertyRow>
            <PropertyRow label={B.windowLength}>{I.windowDays(campaign.window_days)}</PropertyRow>
            <PropertyRow label={B.fromPlan}>{data.plan_id && <Link className="z-link" to="/p/$productId/plans/$planId" params={{ productId, planId: data.plan_id }}>{B.openPlan}</Link>}</PropertyRow>
            <PropertyRow label={A.playbooks}>{playbooks.length > 0 && <span className="z-flow">{playbooks.map((id) => <Link key={id} className="z-link z-mono" to="/playbooks/$playbookId" params={{ playbookId: id }} search={{ product: productId }}>{id}</Link>)}</span>}</PropertyRow>
          </PropertyList>
        </aside>
      </div>}</TabPanel>
      <TabPanel value="table">{view === "table" && product && <CampaignTable product={product} campaign={campaign} />}</TabPanel>
    </Tabs>
  </Page>;
}

function ReadingLine({ reading, zone }: { reading: C.CampaignDetail["readings"][number]; zone: string }) {
  return <div className="z-flow">
    <p className="z-inline">
      <ResultBadge result={reading.value === "no_signal" ? "none" : reading.value} />
      <span>{I.nextSteps[reading.next_step]}</span>
      <span className="z-small z-muted">{B.windowFrom(dateTime(reading.window_start, zone))} · {B.confirmedOn(dateTime(reading.confirmed_at, zone))}</span>
    </p>
    {reading.suggested_value && reading.suggested_value !== reading.value && <p className="z-small z-muted">{B.suggested(STATUS_LABELS[resultStatus(reading.suggested_value)])}</p>}
    {!reading.matured && <p className="z-small z-muted">{B.early}</p>}
    {reading.note && <p>{reading.note}</p>}
  </div>;
}
